import i18next from "i18next";
import { Dropdown, Select } from "flowbite-react";
import { AlertMessage } from "../../../atoms/AlertMessage";
import { Spinner } from "../../../atoms/Spinner";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import useAuth from "../../../../hooks/useAuth";
import ManageCustomerGroups from "./ManageCustomerGroups";
import { useTranslation } from "react-i18next";
import NoTranslate from "../../../atoms/NoTranslate";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SendPersonalMessage from "./SendPersonalMessage";
import { useTenantSendContext } from "./sendPersonalMessage/useTenantSendContext";
import { SEND_MESSAGE_TYPE } from "../../../../constants/types";
import SendSMSModal from "./SendSMSModal";
import SendWhatsAppModal from "./SendWhatsAppModal";
import SendMessageChannelModal from "./SendMessageChannelModal";
import RoleTypeEditor from "./RoleTypeEditor";
import ESignatureSection from "./ESignatureSection";
import GamingHistorySection from "./GamingHistorySection";
import AppointmentsSection from "./AppointmentsSection";
import {
  HiArrowLeft,
  HiBan,
  HiCheckCircle,
  HiEye,
  HiLink,
  HiOutlineClock,
  HiOutlineDocumentSearch,
  HiOutlineDocumentText,
  HiOutlineDotsVertical,
  HiOutlineExclamationCircle,
  HiOutlineKey,
  HiOutlineLockClosed,
  HiOutlinePencil,
  HiOutlineTrash,
  HiThumbUp,
} from "react-icons/hi";
import { HiOutlineEnvelope } from "react-icons/hi2";
import { FaSms } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import { Copy } from "../../../atoms/Copy";
import { StatusBadge } from "../../../atoms/StatusBadge";
import AskAiSearch from "../../../molecules/AskAiSearch";
import { PlansModal } from "../../../modals/PlansModal";
import UpgradeModal from "../../../modals/UpgradeModal";
import { PremiumBenefit } from "../../../atoms/UpgradePromptContent";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import CountryOptions from "../../../public/CountryOptions";
import { countries } from "../../../../utils/countries";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { EmptyState } from "../../../atoms/EmptyState";
import { ModalShell } from "../../../atoms/ModalShell";
import SideDrawer from "../../../atoms/SideDrawer";
import {
  useAdminUpdateUserEmail,
  useConfirmPhoneValidation,
  useDeleteEntityUser,
  useGenratePassword,
  useGetEntityUser,
  useGetPhoneValidationStatus,
  useGetUserPaymentHistory,
  useResendValidationLink,
  useStartPhoneValidation,
  useUpdateUserProfile,
  useUpdateUserProfileAvatar,
  useUserForgotPassword,
  useValidateStatus,
  usersCache,
} from "../../../../hooks/users";
import { useDeleteFiles, useUpdateAvatar } from "../../../../hooks/cdn";
import { useGetEntityPreferencesByPath } from "../../../../hooks/entity";
import { useCommunicationPreferences } from "../../../../hooks/useCommunicationPreferences";
import UserNotes from "./UserNotes";
import UserTracker from "./UserTracker";
import UserEntityTabs from "../UserEntityTabs";
import UserPaymentHistory, {
  CancelSubscriptionInfo,
} from "../users/UserPaymentHistory";
import * as flags from "country-flag-icons/react/3x2";
import { PhoneNumberWithVerification } from "../../../atoms/PhoneNumberWithVerification";
import PhoneValidationModal from "../../../molecules/PhoneValidationModal";
import { AddCreditModal } from "./AddCreditModal";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import { CreatePaymentRequestModal } from "../../../modals/CreatePaymentRequestModal";
import PaymentModuleService, {
  PaymentRequestResponseDTO,
} from "../../../../services/paymentModule";
import ErrorBoundary from "../../../public/ErrorBoundary";
import { SmsStatusEnum } from "../../../../constants/smsStatusEnum";
import ImportFromDocument from "../../../molecules/ImportFromDocument";

export type UserGroup = {
  group_identification: string;
  group_name: string;
};

type EntityUserProfile = {
  user_identification: string;
  user_user_name: string | null;
  user_last_name: string | null;
  user_first_name: string | null;
  user_birth_date: string | null;
  user_avatar: string | null;
  user_creation_date_time: string;
  user_last_connection: string | null;
  user_phone: string | null;
  user_email: string;
  user_country_code: string;
  user_is_active: boolean;
  user_role: string;
  user_type: string;
  user_groups: UserGroup[];
  user_account_status: string;
};

const RESTRICTED_COUNTRY_CODES = [
  "+93",
  "+963",
  "+967",
  "+236",
  "+261",
  "+258",
  "+263",
  "+675",
  "+98",
];

const isRestrictedCountry = (phone: string | null | undefined): boolean => {
  if (!phone) return false;
  return RESTRICTED_COUNTRY_CODES.some((code) => phone.startsWith(code));
};

const schema = Joi.object({
  new_user_user_name: Joi.string()
    .trim()
    .min(3)
    .max(30)
    .optional()
    .allow(null, "")
    .label("Username"),

  new_user_first_name: Joi.string()
    .trim()
    .min(3)
    .max(30)
    .optional()
    .allow(null, "")
    .label("First name"),

  new_user_last_name: Joi.string()
    .trim()
    .min(3)
    .max(150)
    .optional()
    .allow(null, "")
    .label("Last name"),

  new_user_birth_date: Joi.date()
    .optional()
    .max(moment().endOf("day").toDate())
    .allow(null, "")
    .label("Birth date")
    .messages({
      "date.max": "Future dates are not allowed",
    }),

  new_user_phone: Joi.string().trim().optional().allow(null, "").label("Phone"),

  new_user_country_code: Joi.string()
    .trim()
    .optional()
    .allow(null, "")
    .label("Country code"),
});

const ViewEntityUser = () => {
  const { t, i18n } = useTranslation();

  const emailSchema = useMemo(
    () =>
      Joi.object({
        new_user_email: Joi.string()
          .trim()
          .email({ tlds: false })
          .required()
          .label(t("dashboard.entities.viewEntityUser.newEmail"))
          .messages({
            "string.empty": t(
              "dashboard.entities.viewEntityUser.emailRequired"
            ) as string,
            "string.email": t(
              "dashboard.entities.viewEntityUser.invalidEmail"
            ) as string,
          }),
        new_user_email_confirmation: Joi.string()
          .trim()
          .email({ tlds: false })
          .required()
          .valid(Joi.ref("new_user_email"))
          .label(t("dashboard.entities.viewEntityUser.confirmEmail"))
          .messages({
            "string.empty": t(
              "dashboard.entities.viewEntityUser.emailRequired"
            ) as string,
            "string.email": t(
              "dashboard.entities.viewEntityUser.invalidEmail"
            ) as string,
            "any.only": t(
              "dashboard.entities.viewEntityUser.emailConfirmationMustMatch"
            ) as string,
          }),
      }),
    [t]
  );
  const { user, customer_identification, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const hasSecondaryActions =
    permissions.can(Cap.USERS_ACCESS_MANAGEMENT, Level.WRITE) ||
    permissions.can(Cap.USERS_ACTIVITIES, Level.VIEW) ||
    permissions.can(Cap.USERS_ACCESS, Level.WRITE) ||
    permissions.can(Cap.USERS_DELETE, Level.DELETE);
  const { user_identification } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigateType = location.state?.navigateType;

  const [showChannelModal, setShowChannelModal] = useState(false);
  const [showNotesDrawer, setShowNotesDrawer] = useState(false);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const [showSendMsgModal, setShowSendMsgModal] = useState(false);
  const [showSendSMSModal, setShowSendSMSModal] = useState(false);
  const [showSendWhatsAppModal, setShowSendWhatsAppModal] = useState(false);
  const [showWhatsAppAddCreditModal, setShowWhatsAppAddCreditModal] =
    useState(false);
  const [showMessagingUpgradeModal, setShowMessagingUpgradeModal] =
    useState(false);
  const [showSMSUpgradeModal, setShowSMSUpgradeModal] = useState(false);
  const [showWhatsappUpgradeModal, setShowWhatsappUpgradeModal] =
    useState(false);
  const [showAiSearchUpgradeModal, setShowAiSearchUpgradeModal] =
    useState(false);
  const [showAddCreditModal, setShowAddCreditModal] = useState(false);
  const [showPaymentRequestModal, setShowPaymentRequestModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [userToDeleteId, setUserToDeleteId] = useState("");
  const [editData, setEditData] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [openDocumentModal, setOpenDocumentModal] = useState(false);
  const [userPaymentRequests, setUserPaymentRequests] = useState<
    PaymentRequestResponseDTO[]
  >([]);
  const [loadingPaymentRequests, setLoadingPaymentRequests] = useState(false);
  const [deletingPaymentLink, setDeletingPaymentLink] = useState<string | null>(
    null
  );
  const [showDeletePaymentLinkModal, setShowDeletePaymentLinkModal] =
    useState(false);
  const [isDeletingPaymentLink, setIsDeletingPaymentLink] = useState(false);
  const [viewingPaymentRequest, setViewingPaymentRequest] =
    useState<PaymentRequestResponseDTO | null>(null);
  const [highlightedLink, setHighlightedLink] = useState<string | null>(null);
  const [showAllLinksDrawer, setShowAllLinksDrawer] = useState(false);
  const allLinksRef = useRef<HTMLDivElement>(null);

  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] =
    useState(false);
  const [cancellingLink, setCancellingLink] =
    useState<CancelSubscriptionInfo | null>(null);
  const [cancellingMode, setCancellingMode] = useState<
    "immediate" | "end_of_period" | null
  >(null);

  const [phoneValidationStatus, setPhoneValidationStatus] =
    useState<string>("");
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] =
    useState(false);
  const [phoneValidationExpiresAt, setPhoneValidationExpiresAt] =
    useState<number>(0);

  const messagingBenefits: PremiumBenefit[] = [
    {
      id: "unlimited-messaging",
      text: t("dashboard.upgrade.messaging.benefit1") as string,
    },
    {
      id: "bulk-messaging",
      text: t("dashboard.upgrade.messaging.benefit2") as string,
    },
    {
      id: "custom-templates",
      text: t("dashboard.upgrade.messaging.benefit3") as string,
    },
    {
      id: "scheduled-messages",
      text: t("dashboard.upgrade.messaging.benefit4") as string,
    },
  ];

  const whatsappBenefits: PremiumBenefit[] = [
    {
      id: "whatsapp-reach",
      text: t("dashboard.upgrade.whatsapp.benefit1") as string,
    },
    {
      id: "whatsapp-templates",
      text: t("dashboard.upgrade.whatsapp.benefit2") as string,
    },
    {
      id: "whatsapp-pricing",
      text: t("dashboard.upgrade.whatsapp.benefit3") as string,
    },
  ];

  const smsBenefits: PremiumBenefit[] = [
    {
      id: "sms-real-time",
      text: t("dashboard.upgrade.sms.benefit1") as string,
    },
    {
      id: "sms-delivery",
      text: t("dashboard.upgrade.sms.benefit2") as string,
    },
    {
      id: "sms-international",
      text: t("dashboard.upgrade.sms.benefit3") as string,
    },
  ];

  const {
    isLoading,
    isFetching,
    isError,
    data: res,
    refetch,
  } = useGetEntityUser({
    customer_identification: customer_identification,
    entityIdentification: user.entity_identification,
    user_identification: user_identification as string,
  });

  const { mutate: deleteUser } = useDeleteEntityUser();

  const { mutate: resendValidation, isLoading: isResending } =
    useResendValidationLink();

  const { mutate: generatePassword, isLoading: isGenerating } =
    useGenratePassword();

  const { mutate: validateStatus, isLoading: isValidating } =
    useValidateStatus();

  const { mutate: updateProfile, isLoading: isUpdating } =
    useUpdateUserProfile();

  const { mutate: adminUpdateEmail, isLoading: isUpdatingEmail } =
    useAdminUpdateUserEmail();

  const { mutate: forgetPassword, isLoading: isForgeting } =
    useUserForgotPassword();

  const { mutate: updateAvatar, isLoading: isUpdatingAvatar } =
    useUpdateUserProfileAvatar();

  const { mutate: uploadToCdn, isLoading: isUploadingToCdn } =
    useUpdateAvatar();

  const { mutate: deleteFiles } = useDeleteFiles({ notify: false });

  const {
    isSmsBlocked,
    isEmailBlocked,
    isWhatsappBlocked,
    toggle: toggleCommunication,
    isLoading: isLoadingCommPrefs,
    isUpdating: isUpdatingCommPrefs,
  } = useCommunicationPreferences(
    customer_identification,
    user.entity_identification,
    user_identification as string
  );

  const userData = res as EntityUserProfile | undefined;

  const sendContext = useTenantSendContext({
    customer_identification,
    user,
    entity_identification: user.entity_identification,
    entity_user_identification: customer_identification,
    toSendMsgUserId: user_identification as string,
    type: SEND_MESSAGE_TYPE.SINGLE,
    userData,
    isEmailBlocked,
    show: showSendMsgModal,
    close: () => setShowSendMsgModal(false),
  });

  const { data: entityPreferences } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    "keep_unverified_users",
    {
      enabled:
        !!customer_identification &&
        !!user.entity_identification &&
        userData?.user_is_active === false,
      staleTime: Infinity,
    }
  );
  const keepUnverifiedUsers = !!entityPreferences?.keep_unverified_users;

  const {
    data: paymentHistoryData,
    isLoading: isLoadingPaymentHistory,
    isError: isErrorPaymentHistory,
    refetch: refetchPaymentHistory,
  } = useGetUserPaymentHistory(
    customer_identification,
    user.entity_identification,
    user_identification as string,
    !!user_identification
  );

  const { data: userDataSpecial, refetch: refetchUserDataSpecial } =
    useGetPhoneValidationStatus(
      customer_identification,
      user.entity_identification,
      user_identification as string
    );

  const { mutate: startPhoneValidation, isLoading: isStartingPhoneValidation } =
    useStartPhoneValidation();
  const {
    mutate: confirmPhoneValidation,
    isLoading: isConfirmingPhoneValidation,
  } = useConfirmPhoneValidation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    control,
    reset,
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: {
      new_user_first_name: userData?.user_first_name,
      new_user_last_name: userData?.user_last_name,
      new_user_birth_date: userData?.user_birth_date
        ? new Date(userData?.user_birth_date).toISOString().substring(0, 10)
        : "",
      new_user_phone: userData?.user_phone,
      new_user_user_name: userData?.user_user_name,
      new_user_country_code: userData?.user_country_code,
    },
    mode: "onSubmit",
  });

  const {
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors },
    control: emailControl,
    reset: resetEmail,
  } = useForm({
    resolver: joiResolver(emailSchema),
    defaultValues: {
      new_user_email: "",
      new_user_email_confirmation: "",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (userData) {
      reset({
        new_user_first_name: userData.user_first_name || "",
        new_user_last_name: userData.user_last_name || "",
        new_user_birth_date: userData.user_birth_date
          ? new Date(userData.user_birth_date).toISOString().substring(0, 10)
          : "",
        new_user_phone: userData.user_phone || "",
        new_user_user_name: userData.user_user_name || "",
        new_user_country_code: userData.user_country_code || "",
      });
      resetEmail({
        new_user_email: "",
        new_user_email_confirmation: "",
      });
    }
  }, [reset, resetEmail, userData]);

  const onEmailSubmit = (data: any) => {
    adminUpdateEmail(
      {
        customer_identification: customer_identification,
        entity_identification: user.entity_identification,
        user_identification: user_identification,
        data: {
          user_email: userData?.user_email,
          new_user_email: data.new_user_email,
          new_user_email_confirmation: data.new_user_email_confirmation,
        },
      },
      {
        onSuccess: () => {
          setEditEmail(false);
          resetEmail({
            new_user_email: "",
            new_user_email_confirmation: "",
          });
          refetch();
          toast.success(
            t("dashboard.entities.viewEntityUser.emailUpdatePending")
          );
        },
      }
    );
  };

  const fetchUserPaymentRequests = useCallback(async () => {
    if (!user_identification) return;
    try {
      setLoadingPaymentRequests(true);
      const response = await PaymentModuleService.getAllPaymentRequests(
        customer_identification,
        user.entity_identification,
        user_identification
      );
      const userRequests = (response.data?.element || [])
        .filter(
          (r: PaymentRequestResponseDTO) =>
            r.user_identification === user_identification
        )
        .sort((a: PaymentRequestResponseDTO, b: PaymentRequestResponseDTO) => {
          const dateA = a.payment_request_datetime
            ? new Date(a.payment_request_datetime).getTime()
            : 0;
          const dateB = b.payment_request_datetime
            ? new Date(b.payment_request_datetime).getTime()
            : 0;
          return dateB - dateA;
        });
      setUserPaymentRequests(userRequests);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setUserPaymentRequests([]);
      } else {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            "Failed to load payment requests"
        );
        setUserPaymentRequests([]);
      }
    } finally {
      setLoadingPaymentRequests(false);
    }
  }, [
    user_identification,
    customer_identification,
    user.entity_identification,
  ]);

  useEffect(() => {
    if (userData?.user_identification) {
      fetchUserPaymentRequests();
    }
  }, [userData?.user_identification, fetchUserPaymentRequests]);

  // A closed drawer keeps its rows off screen: scrolling to one only shows once the drawer is open.
  useEffect(() => {
    if (!showAllLinksDrawer || !highlightedLink) return;
    allLinksRef.current
      ?.querySelector(`[data-payment-link="${highlightedLink}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [showAllLinksDrawer, highlightedLink]);

  const formatPaymentAmount = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(i18n.language || "en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
      }).format(amount / 100);
    } catch (error) {
      return `${(amount / 100).toFixed(2)} ${currency}`;
    }
  };

  const PAYMENT_LINKS_PREVIEW = 3;

  const highlightPaymentLink = (link: string | null) => {
    setHighlightedLink(link);
    const rank = userPaymentRequests.findIndex(
      (request) => request.payment_unique_link === link
    );
    if (rank >= PAYMENT_LINKS_PREVIEW) setShowAllLinksDrawer(true);
  };

  const renderPaymentLinkRow = (request: PaymentRequestResponseDTO) => (
    <div
      key={request.payment_unique_link}
      data-payment-link={request.payment_unique_link}
      className={`flex items-center justify-between p-4 bg-gray-50 rounded-lg border w-full transition-all duration-300 ${
        highlightedLink === request.payment_unique_link
          ? "border-blue-500 border-2 shadow-lg"
          : "border-gray-100"
      }`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <HiLink size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-700">
              <NoTranslate>
                {formatPaymentAmount(request.amount, request.currency)}
              </NoTranslate>
            </p>
            {request.recurrency && request.recurrency !== "UNIQUE" && (
              <StatusBadge color="gray" shape="full">
                {request.recurrency === "MONTHLY"
                  ? t("paymentModule.recurrencyBadge.monthly")
                  : t("paymentModule.recurrencyBadge.annually")}
              </StatusBadge>
            )}
          </div>
          <p className="text-xs text-gray-400 truncate mt-1">
            <NoTranslate>{request.payment_link}</NoTranslate>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
        <CustomButton
          variant="none"
          size="icon"
          onClick={() => setViewingPaymentRequest(request)}
          className="rounded-md text-gray-400 transition-colors enabled:hover:text-blue-600"
          tooltip={t("common.viewDetails") as string}
          icon={<HiEye size={18} />}
        />
        {permissions.can(Cap.CREATE_PAYMENT_LINK, Level.WRITE) && (
          <CustomButton
            variant="none"
            size="icon"
            onClick={() => {
              setDeletingPaymentLink(request.payment_unique_link);
              setShowDeletePaymentLinkModal(true);
            }}
            className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
            tooltip={t("common.delete") as string}
            icon={<HiOutlineTrash size={18} />}
          />
        )}
      </div>
    </div>
  );

  const handleDeletePaymentLink = async () => {
    if (!deletingPaymentLink) return;
    try {
      setIsDeletingPaymentLink(true);

      if (highlightedLink === deletingPaymentLink) {
        setHighlightedLink(null);
      }

      await PaymentModuleService.deletePaymentRequest(
        customer_identification,
        user.entity_identification,
        deletingPaymentLink
      );

      await fetchUserPaymentRequests();

      toast.success(
        t("dashboard.entities.viewEntityUser.deletePaymentLink.success")
      );
      setShowDeletePaymentLinkModal(false);
      setDeletingPaymentLink(null);
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("dashboard.entities.viewEntityUser.deletePaymentLink.error")
      );
    } finally {
      setIsDeletingPaymentLink(false);
    }
  };

  const handleCancelSubscription = async (cancelImmediately: boolean) => {
    if (!cancellingLink) return;
    try {
      setCancellingMode(cancelImmediately ? "immediate" : "end_of_period");
      await PaymentModuleService.cancelPaymentRequestSubscription(
        customer_identification,
        user.entity_identification,
        cancellingLink.uniqueLink,
        cancelImmediately
      );
      toast.success(
        cancelImmediately
          ? t("paymentModule.cancelSubscriptionModal.cancelImmediateSuccess")
          : t("paymentModule.cancelSubscriptionModal.cancelEndOfPeriodSuccess")
      );
      setShowCancelSubscriptionModal(false);
      setCancellingLink(null);
      fetchUserPaymentRequests();
      usersCache.delete(`user_payment_history_grouped_${user_identification}`);
      refetchPaymentHistory();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.cancelSubscriptionModal.cancelFailed")
      );
    } finally {
      setCancellingMode(null);
    }
  };

  const handleDeleteUser = () => {
    if (userToDeleteId) {
      deleteUser(
        {
          customer_identification: customer_identification,
          entity_identification: user.entity_identification,
          user_identification: userToDeleteId,
        },
        {
          onSuccess: () => {
            navigateType === "Groups"
              ? navigate(-1)
              : navigate("/control-panel/users");
          },
        }
      );
    }
  };

  const handleResendValidationLink = (userId: string) => {
    resendValidation({
      customer_identification: customer_identification,
      entity_identification: user.entity_identification,
      user_identification: userId,
    });
  };

  useEffect(() => {
    if (userDataSpecial && userDataSpecial.status) {
      const validationData = userDataSpecial;
      const currentPhoneNumber = userData?.user_phone;
      const validatedPhoneNumber = validationData.phoneNumber;

      if (
        validationData.status === SmsStatusEnum.VALIDATED &&
        currentPhoneNumber === validatedPhoneNumber
      ) {
        setPhoneValidationStatus(SmsStatusEnum.VALIDATED);
      } else {
        setPhoneValidationStatus("");
      }
    } else {
      setPhoneValidationStatus("");
    }
  }, [userDataSpecial, userData?.user_phone]);

  const hasMessageAccess = permissions.hasFeatureAccess("SEND_MESSAGES");
  const hasSMSAccess = permissions.hasFeatureAccess("SEND_SMS");
  const hasWhatsappAccess = permissions.hasFeatureAccess("SEND_WHATSAPP");
  const canSendEmail = permissions.can(Cap.SEND_EMAIL, Level.WRITE);
  const canSendSms = permissions.can(Cap.SEND_SMS, Level.WRITE);
  const canSendWhatsapp = permissions.can(Cap.SEND_WHATSAPP, Level.WRITE);
  const hasPhoneValidationAccess = permissions.hasFeatureAccess(
    "PHONE_NUMBER_VALIDATION"
  );
  const hasESignatureAccess = permissions.hasFeatureAccess("ESIGNATURE_ACCESS");
  const canViewGroups = permissions.can(Cap.GROUP_MANAGEMENT, Level.VIEW);
  const canViewPaymentHistory = permissions.can(
    Cap.CREATE_PAYMENT_LINK,
    Level.VIEW
  );
  const canViewESignature = permissions.can(
    Cap.SEND_FOR_ESIGNATURE,
    Level.VIEW
  );

  const handleStartPhoneVerification = () => {
    startPhoneValidation(
      {
        customer_identification,
        entityIdentification: user.entity_identification,
        user_identification: user_identification as string,
      },
      {
        onSuccess: (data) => {
          const expiresAt =
            data?.data?.element?.expiredAt || Date.now() + 5 * 60 * 1000;
          setPhoneValidationExpiresAt(expiresAt);
          setShowPhoneVerificationModal(true);
        },
      }
    );
  };

  const handleConfirmPhoneVerification = (code: string) => {
    confirmPhoneValidation(
      {
        customer_identification,
        entityIdentification: user.entity_identification,
        user_identification: user_identification as string,
        code,
      },
      {
        onSuccess: () => {
          setShowPhoneVerificationModal(false);
          setPhoneValidationStatus(SmsStatusEnum.VALIDATED);
          refetchUserDataSpecial();
        },
      }
    );
  };

  if (isLoading)
    return (
      <span translate="no">
        <Spinner size="sm" />
      </span>
    );

  if (isError)
    return (
      <CustomButton
        onClick={() => refetch()}
        title={t("dashboard.entities.viewEntityUser.btn-01") as string}
      />
    );

  if (!userData)
    return (
      <NoTranslate>
        <Spinner size="sm" />
      </NoTranslate>
    );

  const onSubmit = (data: any) => {
    updateProfile(
      {
        customer_identification: customer_identification,
        entity_identification: user.entity_identification,
        user_identification: user_identification,
        data: data,
      },
      {
        onSuccess: () => {
          setEditData(false);
        },
      }
    );
  };

  const banUnBanHandler = (userStatus: string, userId: string) => {
    validateStatus({
      customer_identification: customer_identification,
      entity_identification: user.entity_identification,
      userId: userId,
      status: userStatus === "BANNED" ? "OK" : "BANNED",
    });
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const oldAvatarUrl = userData.user_avatar;
      const oldFileName = oldAvatarUrl ? oldAvatarUrl.split("/").pop() : null;

      const formData = new FormData();
      formData.append("image", file);
      uploadToCdn(
        {
          user_identification: customer_identification,
          entity_identification: user.entity_identification,
          data: formData,
        },
        {
          onSuccess: (cdnResponse) => {
            const avatarUrl = cdnResponse.data?.element?.[0]?.cdnPath;
            if (avatarUrl) {
              updateAvatar(
                {
                  customer_identification: customer_identification,
                  entity_identification: user.entity_identification,
                  user_identification: user_identification,
                  data: { avatar: avatarUrl },
                },
                {
                  onSuccess: () => {
                    if (
                      oldFileName &&
                      oldAvatarUrl &&
                      oldAvatarUrl.startsWith("https")
                    ) {
                      deleteFiles({
                        user_identification: customer_identification,
                        entity_identification: user.entity_identification,
                        files: [oldFileName],
                      });
                    }

                    event.target.value = "";
                    refetch();
                  },
                }
              );
            }
          },
        }
      );
    }
  };

  const CountryFlag = userData?.user_country_code
    ? (flags as any)[userData.user_country_code.toUpperCase()]
    : null;

  return (
    <div className="entity-user-page">
      <div className="grid grid-cols-11">
        <div className="lg:col-span-11 col-span-12">
          <div className="p-3 sm:p-6 flex-col justify-start items-start gap-6 inline-flex bg-gray-50 w-full">
            <div className="self-stretch lg:justify-start lg:items-start justify-between items-center gap-2 inline-flex">
              <button
                onClick={() => {
                  navigateType === "Groups"
                    ? navigate(-1)
                    : navigate("/control-panel/users");
                }}
                className="gap-2 inline-flex items-center"
              >
                <div className="w-5 h-5 relative">
                  <HiArrowLeft size={20} className="text-gray-400" />
                </div>
                <div className="grow shrink basis-0 text-gray-500 text-sm font-semibold font-figtree leading-tight">
                  {navigateType === "Groups"
                    ? t("dashboard.groupList.backToGroupUsers")
                    : t("dashboard.entities.viewEntityUser.key1")}
                </div>
              </button>
            </div>

            <div className="self-stretch flex flex-col sm:flex-row sm:flex-wrap sm:items-start gap-4">
              <div
                className="w-16 h-16 shrink-0 relative bg-gray-100 rounded-full group cursor-pointer"
                onClick={() => {
                  if (!permissions.can(Cap.USERS_UPDATE, Level.WRITE)) return;
                  document.getElementById("avatar-upload-input")?.click();
                }}
              >
                <img
                  className="w-16 h-16 relative rounded-full object-cover"
                  src={userData.user_avatar || "/images/avatar.png"}
                  alt="avatar"
                />

                {permissions.can(Cap.USERS_UPDATE, Level.WRITE) && (
                  <div className="absolute bottom-0 right-0 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 group-hover:bg-blue-700">
                    <HiOutlinePencil className="w-3 h-3 text-white" />
                  </div>
                )}

                {(isUploadingToCdn || isUpdatingAvatar) && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <Spinner size="sm" className="fill-white" />
                  </div>
                )}

                <input
                  id="avatar-upload-input"
                  type="file"
                  accept="image/png,image/jpg,image/jpeg"
                  onChange={handleAvatarUpload}
                  disabled={
                    isUploadingToCdn ||
                    isUpdatingAvatar ||
                    !permissions.can(Cap.USERS_UPDATE, Level.WRITE)
                  }
                  className="hidden"
                />
              </div>
              <div className="min-w-0 flex-1 sm:basis-64 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div
                    className="min-w-0 truncate text-xl font-semibold text-gray-900 leading-tight"
                    title={
                      userData.user_first_name && userData.user_last_name
                        ? userData.user_first_name.toLocaleUpperCase() +
                          " " +
                          userData.user_last_name.toLocaleUpperCase()
                        : (userData.user_email as string)
                    }
                  >
                    <NoTranslate>
                      {userData.user_first_name && userData.user_last_name
                        ? userData.user_first_name.toLocaleUpperCase() +
                          " " +
                          userData.user_last_name.toLocaleUpperCase()
                        : (userData.user_email as string)}
                    </NoTranslate>
                  </div>

                  {userData.user_is_active ? (
                    <StatusBadge color="success" size="sm">
                      {t("dashboard.entities.viewEntityUser.status-01")}
                    </StatusBadge>
                  ) : (
                    <StatusBadge color="warning" size="sm">
                      {t("dashboard.entities.viewEntityUser.status-02")}
                    </StatusBadge>
                  )}
                  {userData.user_account_status === "BANNED" ? (
                    <StatusBadge color="failure" size="sm">
                      {t("dashboard.entities.viewEntityUser.key4")}
                    </StatusBadge>
                  ) : userData.user_account_status === "OK" ? (
                    <StatusBadge color="success" size="sm">
                      {t("dashboard.usersList.item-11")}
                    </StatusBadge>
                  ) : null}
                </div>

                {!!userData.user_user_name && (
                  <div className="text-gray-500 text-sm font-normal font-figtree leading-none">
                    <NoTranslate>#{userData.user_user_name}</NoTranslate>
                  </div>
                )}

                {!!userData.user_email && (
                  <div className="text-gray-500 text-sm font-normal font-figtree leading-none flex items-center gap-2 flex-wrap">
                    {t("dashboard.entities.viewEntityUser.item-10")}:{" "}
                    <Copy
                      target={userData.user_email as string}
                      truncate
                      longVersion
                    />
                    {permissions.can(Cap.USERS_UPDATE, Level.WRITE) && (
                      <button
                        onClick={() => setEditEmail(true)}
                        title={
                          t(
                            "dashboard.userProfile.profileForm.btn-01"
                          ) as string
                        }
                      >
                        <HiOutlinePencil className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 font-figtree">
                  <span className="inline-flex items-center gap-1">
                    {t("dashboard.entities.viewEntityUser.key12")}{" "}
                    <Copy
                      target={userData.user_identification as string}
                      truncate
                    />
                  </span>
                  <span
                    title={moment
                      .utc(userData.user_creation_date_time)
                      .local()
                      .format("LLLL")}
                  >
                    {t("dashboard.entities.viewEntityUser.item-06")}:{" "}
                    {moment
                      .utc(userData.user_creation_date_time)
                      .local()
                      .format("lll")}
                  </span>
                  {userData.user_last_connection && (
                    <span
                      title={moment
                        .utc(userData.user_last_connection)
                        .local()
                        .format("LLLL")}
                    >
                      {t("dashboard.entities.viewEntityUser.item-07")}:{" "}
                      {moment
                        .utc(userData.user_last_connection)
                        .local()
                        .format("lll")}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {(permissions.can(Cap.SEND_EMAIL, Level.WRITE) ||
                  permissions.can(Cap.SEND_SMS, Level.WRITE)) && (
                  <CustomButton
                    data-testid="send-message-action"
                    onClick={() => setShowChannelModal(true)}
                    variant="primary"
                    icon="envelop"
                    title={t(
                      "dashboard.entities.viewEntityUser.sendMsg.actionBtn"
                    )}
                  />
                )}

                {permissions.can(Cap.USER_NOTES, Level.VIEW) && (
                  <button
                    type="button"
                    data-testid="customer-notes-link"
                    onClick={() => setShowNotesDrawer(true)}
                    className="bg-white rounded-md shadow-sm border border-gray-300 flex justify-center items-center text-gray-700 text-sm font-medium gap-2 truncate px-3 min-h-[44px] sm:min-h-[38px] transition-colors hover:bg-gray-50"
                  >
                    <HiOutlineDocumentText
                      size={22}
                      className="text-gray-400 shrink-0"
                    />
                    <span className="hidden sm:inline">
                      {t("dashboard.entities.viewEntityUser.key17")}
                    </span>
                  </button>
                )}

                {permissions.can(Cap.READ_A_DOCUMENT, Level.VIEW) && (
                  <button
                    type="button"
                    data-testid="read-document-action"
                    onClick={() => setOpenDocumentModal(true)}
                    className="bg-white rounded-md shadow-sm border border-gray-300 flex justify-center items-center text-gray-700 text-sm font-medium gap-2 truncate px-3 min-h-[44px] sm:min-h-[38px] transition-colors hover:bg-gray-50"
                  >
                    <HiOutlineDocumentSearch
                      size={22}
                      className="text-gray-400 shrink-0"
                    />
                    <span className="hidden sm:inline">
                      {t("dashboard.entities.viewEntityUser.readDocument")}
                    </span>
                  </button>
                )}

                {hasSecondaryActions && (
                  <Dropdown
                    label=""
                    placement="bottom-end"
                    renderTrigger={() => (
                      <button className="bg-white rounded-md shadow-sm border border-gray-300 flex justify-center items-center px-3 min-h-[44px] sm:min-h-[38px] min-w-[44px] sm:min-w-0 transition-colors hover:bg-gray-50">
                        <HiOutlineDotsVertical
                          size={22}
                          className="text-gray-400 shrink-0"
                        />
                      </button>
                    )}
                    className="font-medium font-figtree leading-tight"
                  >
                    {permissions.can(
                      Cap.USERS_ACCESS_MANAGEMENT,
                      Level.WRITE
                    ) && (
                      <Dropdown.Item
                        onClick={() => {
                          generatePassword({
                            user_identification: userData.user_identification,
                            customer_identification: customer_identification,
                            entity_identification: user.entity_identification,
                          });
                        }}
                        disabled={isGenerating || userEndedSubscription}
                      >
                        <HiOutlineKey
                          size={20}
                          className="mr-2 text-gray-400"
                        />
                        {isGenerating
                          ? t("dashboard.entities.viewEntityUser.generating")
                          : t("dashboard.entities.viewEntityUser.btn-02")}
                      </Dropdown.Item>
                    )}

                    {permissions.can(
                      Cap.USERS_ACCESS_MANAGEMENT,
                      Level.WRITE
                    ) && (
                      <Dropdown.Item
                        onClick={() => {
                          forgetPassword({
                            entityIdentification: user.entity_identification,
                            user_email: res?.user_email,
                          });
                        }}
                        disabled={isForgeting || userEndedSubscription}
                      >
                        <HiOutlineLockClosed
                          size={20}
                          className="mr-2 text-gray-400"
                        />
                        {isForgeting
                          ? t("dashboard.entities.viewEntityUser.sending")
                          : t("forgotPassword.label")}
                      </Dropdown.Item>
                    )}

                    {permissions.can(Cap.USERS_ACTIVITIES, Level.VIEW) && (
                      <Dropdown.Item
                        data-testid="customer-history-link"
                        onClick={() => setShowHistoryDrawer(true)}
                      >
                        <HiOutlineClock
                          size={20}
                          className="mr-2 text-gray-400 shrink-0"
                        />
                        {t("dashboard.entities.viewEntityUser.key2")}
                      </Dropdown.Item>
                    )}

                    {!userData.user_is_active &&
                      permissions.can(
                        Cap.USERS_ACCESS_MANAGEMENT,
                        Level.WRITE
                      ) && (
                        <Dropdown.Item
                          onClick={() =>
                            handleResendValidationLink(
                              userData.user_identification
                            )
                          }
                          disabled={isResending || userEndedSubscription}
                        >
                          <HiOutlineEnvelope
                            size={20}
                            className="mr-2 text-gray-400"
                          />
                          {isResending
                            ? t("dashboard.entities.viewEntityUser.sending")
                            : t("dashboard.entities.viewEntityUser.btn-03")}
                        </Dropdown.Item>
                      )}

                    {permissions.can(Cap.USERS_ACCESS, Level.WRITE) && (
                      <Dropdown.Item
                        onClick={() =>
                          banUnBanHandler(
                            userData?.user_account_status,
                            userData.user_identification
                          )
                        }
                        disabled={isValidating || isLoading || isFetching}
                      >
                        {userData?.user_account_status === "BANNED" ? (
                          <>
                            <HiThumbUp
                              size={22}
                              className="mr-2 text-green-400"
                            />
                            {t("dashboard.entities.viewEntityUser.btn-04")}
                          </>
                        ) : (
                          <>
                            <HiBan size={20} className="mr-2 text-red-400" />
                            {t("dashboard.entities.viewEntityUser.btn-05")}
                          </>
                        )}
                      </Dropdown.Item>
                    )}

                    {permissions.can(Cap.USERS_DELETE, Level.DELETE) && (
                      <Dropdown.Item
                        onClick={() => {
                          setUserToDeleteId(userData.user_identification);
                          setOpenModal(true);
                        }}
                      >
                        <HiOutlineTrash
                          size={20}
                          className="mr-2 text-red-400"
                        />
                        {t("dashboard.entities.viewEntityUser.key5")}
                      </Dropdown.Item>
                    )}
                  </Dropdown>
                )}
              </div>
            </div>

            {!userData.user_is_active && (
              <AlertMessage color="warning" className="w-full">
                <div className="flex flex-col gap-1">
                  <span className="font-medium">
                    {t(
                      "dashboard.entities.viewEntityUser.unverifiedAlert.title"
                    )}
                  </span>
                  <span className="text-sm">
                    {t(
                      keepUnverifiedUsers
                        ? "dashboard.entities.viewEntityUser.unverifiedAlert.descriptionKept"
                        : "dashboard.entities.viewEntityUser.unverifiedAlert.description"
                    )}
                  </span>
                </div>
              </AlertMessage>
            )}

            {user_identification &&
              (!permissions.isRoleGoverned() ||
                permissions.can(Cap.AI_ENGINE_SEARCH, Level.VIEW)) && (
                <div className="self-stretch">
                  <AskAiSearch
                    userIdentification={user_identification}
                    compact
                    locked={!permissions.hasFeatureAccess("AI_ENGINE")}
                    onUnlock={() => setShowAiSearchUpgradeModal(true)}
                  />
                </div>
              )}

            <div className="self-stretch bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center gap-3 border-b border-gray-100 pb-3 mb-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("dashboard.entities.viewEntityUser.identity")}
                </h3>
                {!editData &&
                  permissions.can(Cap.USERS_UPDATE, Level.WRITE) && (
                    <CustomButton
                      variant="secondary"
                      size="sm"
                      icon="pencil"
                      className="ml-auto"
                      onClick={() => setEditData(true)}
                      title={
                        t("dashboard.userProfile.profileForm.btn-01") as string
                      }
                    />
                  )}
              </div>
              <form onSubmit={handleSubmit(onSubmit)} className="w-full">
                <div className="self-stretch flex-col justify-start items-start gap-6 flex">
                  <div className="grid grid-cols-12 self-stretch justify-start items-start gap-6">
                    <div className="lg:col-span-4 col-span-12 min-w-0 w-full shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
                      <div className="self-stretch text-gray-500 text-xs font-medium font-figtree uppercase tracking-wide leading-none">
                        {t("dashboard.entities.viewEntityUser.item-03")}
                      </div>

                      {editData ? (
                        <>
                          <CustomInput
                            placeholder=""
                            name="new_user_first_name"
                            control={control}
                            error={errors.new_user_first_name}
                            helperText={errors.new_user_first_name?.message}
                            disabled={!editData}
                            maxLength={30}
                          />
                          <CharacterCounter
                            currentLength={
                              watch("new_user_first_name")?.length || 0
                            }
                            warningThreshold={1}
                            maxLength={30}
                          />
                        </>
                      ) : (
                        <div className="self-stretch min-w-0 max-w-full text-gray-700 text-sm font-medium font-figtree leading-normal">
                          {getValues("new_user_first_name") && (
                            <Copy
                              target={
                                getValues("new_user_first_name") as string
                              }
                            />
                          )}
                          {!getValues("new_user_first_name") && "-"}
                        </div>
                      )}
                    </div>
                    <div className="lg:col-span-4 col-span-12 min-w-0 w-full shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
                      <div className="self-stretch text-gray-500 text-xs font-medium font-figtree uppercase tracking-wide leading-none">
                        {t("dashboard.entities.viewEntityUser.item-04")}
                      </div>

                      {editData ? (
                        <>
                          <CustomInput
                            placeholder=""
                            name="new_user_last_name"
                            control={control}
                            error={errors.new_user_last_name}
                            helperText={errors.new_user_last_name?.message}
                            disabled={!editData}
                            maxLength={30}
                          />
                          <CharacterCounter
                            currentLength={
                              watch("new_user_last_name")?.length || 0
                            }
                            warningThreshold={1}
                            maxLength={30}
                          />
                        </>
                      ) : (
                        <div className="self-stretch min-w-0 max-w-full text-gray-700 text-sm font-medium font-figtree leading-normal">
                          {getValues("new_user_last_name") && (
                            <Copy
                              target={getValues("new_user_last_name") as string}
                            />
                          )}
                          {!getValues("new_user_last_name") && "-"}
                        </div>
                      )}
                    </div>
                    <div className="lg:col-span-4 col-span-12 min-w-0 w-full shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
                      <div className="self-stretch text-gray-500 text-xs font-medium font-figtree uppercase tracking-wide leading-none">
                        {t("dashboard.entities.viewEntityUser.item-02")}
                      </div>

                      {editData ? (
                        <>
                          <CustomInput
                            placeholder=""
                            name="new_user_user_name"
                            control={control}
                            error={errors.new_user_user_name}
                            helperText={errors.new_user_user_name?.message}
                            disabled={!editData}
                            maxLength={30}
                          />
                          <CharacterCounter
                            currentLength={
                              watch("new_user_user_name")?.length || 0
                            }
                            warningThreshold={1}
                            maxLength={30}
                          />
                        </>
                      ) : (
                        <div className="self-stretch min-w-0 max-w-full text-gray-700 text-sm font-medium font-figtree leading-normal">
                          {getValues("new_user_user_name") && (
                            <Copy
                              target={getValues("new_user_user_name") as string}
                            />
                          )}
                          {!getValues("new_user_user_name") && "-"}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-12 self-stretch justify-start items-start gap-6">
                    <div className="lg:col-span-4 col-span-12 min-w-0 shrink basis-0 flex-col justify-start items-start gap-1 inline-flex w-full">
                      <div className="self-stretch text-gray-500 text-xs font-medium font-figtree uppercase tracking-wide leading-none">
                        {t("dashboard.userProfile.profileForm.label-05")}
                      </div>
                      {editData ? (
                        <Select
                          translate="no"
                          id="new_user_country_code"
                          color={
                            !!errors.new_user_country_code ? "failure" : "gray"
                          }
                          className="shadow rounded-md"
                          {...register("new_user_country_code")}
                        >
                          <option value="">
                            {t(
                              "dashboard.userProfile.profileForm.select-option"
                            )}
                          </option>
                          <CountryOptions />
                        </Select>
                      ) : (
                        <div
                          translate="no"
                          className="self-stretch text-gray-700 text-sm font-medium font-figtree leading-normal flex items-center gap-2"
                        >
                          {CountryFlag && (
                            <CountryFlag
                              className="w-8 h-6 rounded-sm shadow-sm border border-gray-200 shrink-0"
                              title={userData.user_country_code || ""}
                            />
                          )}
                          <span className="truncate">
                            {countries.find(
                              (x) =>
                                x.code === getValues("new_user_country_code")
                            )?.name || "-"}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="lg:col-span-4 col-span-12 min-w-0 shrink basis-0 flex-col justify-start items-start gap-1 inline-flex w-full">
                      <div className="self-stretch text-gray-500 text-xs font-medium font-figtree uppercase tracking-wide leading-none">
                        {t("dashboard.entities.viewEntityUser.item-09")}
                      </div>
                      {editData &&
                      permissions.can(Cap.USERS_UPDATE, Level.WRITE) ? (
                        <PhoneNumberWithVerification
                          name="new_user_phone"
                          control={control}
                          error={errors.new_user_phone}
                          helperText={errors.new_user_phone?.message}
                          disabled={!editData}
                          maxLength={13}
                          defaultCountryCode={watch("new_user_country_code")}
                          userIdentification={user_identification as string}
                          entityIdentification={user.entity_identification}
                          customerIdentification={customer_identification}
                          userIsActive={userData.user_is_active}
                          hasPhoneValidationAccess={hasPhoneValidationAccess}
                          onVerificationComplete={() => {
                            refetch();
                            refetchUserDataSpecial();
                          }}
                        />
                      ) : (
                        <div className="self-stretch flex flex-wrap items-center gap-2">
                          <div className="text-gray-700 text-sm font-medium font-figtree leading-normal flex min-w-0 items-center gap-2">
                            {getValues("new_user_phone") &&
                              !getValues("new_user_phone")?.startsWith("+") && (
                                <HiOutlineExclamationCircle
                                  className="text-amber-500 flex-shrink-0"
                                  size={20}
                                  title={
                                    t(
                                      "dashboard.entities.viewEntityUser.phoneWarning"
                                    ) as string
                                  }
                                />
                              )}
                            {getValues("new_user_phone") &&
                              getValues("new_user_phone")?.startsWith("+") &&
                              isRestrictedCountry(
                                getValues("new_user_phone")
                              ) && (
                                <HiOutlineExclamationCircle
                                  className="text-red-500 flex-shrink-0"
                                  size={20}
                                  title={
                                    t(
                                      "phoneValidation.restrictedCountry"
                                    ) as string
                                  }
                                />
                              )}
                            {getValues("new_user_phone") &&
                              phoneValidationStatus ===
                                SmsStatusEnum.VALIDATED && (
                                <HiCheckCircle
                                  className="text-green-500 flex-shrink-0"
                                  size={20}
                                  title={
                                    t("phoneValidation.phoneVerified") as string
                                  }
                                />
                              )}
                            {getValues("new_user_phone") && (
                              <Copy
                                target={getValues("new_user_phone") as string}
                              />
                            )}
                            {!getValues("new_user_phone") && "-"}
                          </div>
                          {permissions.can(Cap.USERS_UPDATE, Level.WRITE) &&
                            getValues("new_user_phone") &&
                            getValues("new_user_phone")?.startsWith("+") &&
                            !isRestrictedCountry(getValues("new_user_phone")) &&
                            hasPhoneValidationAccess &&
                            userData.user_is_active &&
                            phoneValidationStatus !==
                              SmsStatusEnum.VALIDATED && (
                              <CustomButton
                                variant="primary"
                                size="sm"
                                onClick={handleStartPhoneVerification}
                                disabled={isStartingPhoneValidation}
                                className="w-fit shrink-0 whitespace-nowrap"
                                tooltip={
                                  t("phoneValidation.sendCodeTooltip") as string
                                }
                                title={
                                  isStartingPhoneValidation
                                    ? t("phoneValidation.sending")
                                    : t("phoneValidation.verifyPhoneButton")
                                }
                              />
                            )}
                        </div>
                      )}
                    </div>
                    <div className="lg:col-span-4 col-span-12 min-w-0 shrink basis-0 flex-col justify-start items-start gap-1 inline-flex w-full">
                      <div className="self-stretch text-gray-500 text-xs font-medium font-figtree uppercase tracking-wide leading-none">
                        {t("dashboard.entities.viewEntityUser.item-05")}
                      </div>

                      {editData ? (
                        <CustomInput
                          type="date"
                          placeholder=""
                          name="new_user_birth_date"
                          control={control}
                          error={errors.new_user_birth_date}
                          helperText={errors.new_user_birth_date?.message}
                          disabled={!editData}
                          max={moment().format("YYYY-MM-DD")}
                          className="shadow rounded-md max-h-[42px] w-full"
                        />
                      ) : (
                        <div className="self-stretch min-w-0 max-w-full text-gray-700 text-sm font-medium font-figtree leading-normal truncate">
                          {getValues("new_user_birth_date")
                            ? moment(getValues("new_user_birth_date")).format(
                                "LL"
                              )
                            : "-"}
                        </div>
                      )}
                    </div>
                  </div>

                  {editData && (
                    <div className="flex flex-row gap-2">
                      <CustomButton
                        variant="gray"
                        onClick={() => setEditData(false)}
                        title={t("common.cancel") as string}
                      />
                      <CustomButton
                        type="submit"
                        variant="primary"
                        disabled={isUpdating}
                        isLoading={isUpdating}
                        title={t("common.saveShort") as string}
                      />
                    </div>
                  )}
                </div>
              </form>

              <div className="border-t border-gray-100 pt-4 mt-4">
                <div className="text-gray-500 text-xs font-medium font-figtree uppercase tracking-wide leading-none mb-3">
                  {t("dashboard.entities.viewEntityUser.roleAndType")}
                </div>
                <RoleTypeEditor
                  data={{ role: userData.user_role, type: userData.user_type }}
                  entity_identification={user.entity_identification as string}
                  user_identification={customer_identification}
                  customerId={user_identification as string}
                />
              </div>
            </div>

            <div
              className={`grid grid-cols-1 gap-6 self-stretch ${
                canViewGroups ? "md:grid-cols-2" : ""
              }`}
            >
              <div className="min-w-0 bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <div className="w-full">
                  <div className="flex items-center gap-3 border-b border-gray-100 pb-3 mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {t(
                        "dashboard.entities.viewEntityUser.communicationPreferences.title"
                      )}
                    </h3>
                  </div>
                  {isLoadingCommPrefs ? (
                    <span translate="no">
                      <Spinner size="sm" />
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      <label className="flex items-center gap-3 cursor-pointer whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSmsBlocked}
                          onChange={(e) =>
                            toggleCommunication("sms", e.target.checked)
                          }
                          disabled={
                            isUpdatingCommPrefs ||
                            !permissions.can(Cap.USERS_UPDATE, Level.WRITE)
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          {t(
                            "dashboard.entities.viewEntityUser.communicationPreferences.blockSms"
                          )}
                        </span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isEmailBlocked}
                          onChange={(e) =>
                            toggleCommunication("email", e.target.checked)
                          }
                          disabled={
                            isUpdatingCommPrefs ||
                            !permissions.can(Cap.USERS_UPDATE, Level.WRITE)
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          {t(
                            "dashboard.entities.viewEntityUser.communicationPreferences.blockEmail"
                          )}
                        </span>
                      </label>
                      {hasWhatsappAccess && (
                        <label className="flex items-center gap-3 cursor-pointer whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isWhatsappBlocked}
                            onChange={(e) =>
                              toggleCommunication("whatsapp", e.target.checked)
                            }
                            disabled={
                              isUpdatingCommPrefs ||
                              !permissions.can(Cap.USERS_UPDATE, Level.WRITE)
                            }
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            {t(
                              "dashboard.entities.viewEntityUser.communicationPreferences.blockWhatsapp"
                            )}
                          </span>
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {canViewGroups && (
                <div className="min-w-0 bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                  <ManageCustomerGroups
                    data={userData.user_groups}
                    entity_identification={user.entity_identification as string}
                    user_identification={customer_identification}
                    customerId={user_identification as string}
                  />
                </div>
              )}
            </div>

            {(canViewPaymentHistory || canViewESignature) && (
              <div
                className={`grid grid-cols-1 gap-6 self-stretch ${
                  canViewPaymentHistory && canViewESignature
                    ? "md:grid-cols-2"
                    : ""
                }`}
              >
                {canViewPaymentHistory && (
                  <div className="min-w-0 self-stretch bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                    <ErrorBoundary>
                      <div className="flex flex-col w-full">
                        <div className="flex items-center gap-3 border-b border-gray-100 pb-3 mb-4">
                          <h2 className="text-sm font-semibold text-gray-900">
                            {t(
                              "dashboard.entities.viewEntityUser.paymentLinks"
                            )}
                          </h2>
                          {permissions.can(
                            Cap.CREATE_PAYMENT_LINK,
                            Level.WRITE
                          ) &&
                            (loadingPaymentRequests ||
                              userPaymentRequests.length > 0) && (
                              <CustomButton
                                onClick={() => setShowPaymentRequestModal(true)}
                                variant="outline"
                                size="sm"
                                icon="plus"
                                className="ml-auto shrink-0"
                                tooltip={
                                  t(
                                    "dashboard.entities.viewEntityUser.createPaymentLink"
                                  ) as string
                                }
                                title={t(
                                  "dashboard.entities.viewEntityUser.newPaymentLink"
                                )}
                              />
                            )}
                        </div>

                        {loadingPaymentRequests ? (
                          <p className="py-2 text-sm text-gray-400">
                            {t(
                              "dashboard.entities.viewEntityUser.loadingPaymentLinks"
                            )}
                          </p>
                        ) : userPaymentRequests.length > 0 ? (
                          <>
                            <div
                              className="space-y-3"
                              data-payment-links-section
                            >
                              {userPaymentRequests
                                .slice(0, PAYMENT_LINKS_PREVIEW)
                                .map((request) =>
                                  renderPaymentLinkRow(request)
                                )}
                            </div>

                            {userPaymentRequests.length >
                              PAYMENT_LINKS_PREVIEW && (
                              <button
                                type="button"
                                onClick={() => setShowAllLinksDrawer(true)}
                                className="mt-4 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700"
                              >
                                {t(
                                  "dashboard.entities.viewEntityUser.viewAllPaymentLinks",
                                  { count: userPaymentRequests.length }
                                )}
                              </button>
                            )}
                          </>
                        ) : (
                          <EmptyState
                            icon={<HiLink size={24} />}
                            title={t(
                              "dashboard.entities.viewEntityUser.noPaymentLinks"
                            )}
                            description={t(
                              "dashboard.entities.viewEntityUser.noPaymentLinksHint"
                            )}
                            action={
                              permissions.can(
                                Cap.CREATE_PAYMENT_LINK,
                                Level.WRITE
                              ) && (
                                <CustomButton
                                  onClick={() =>
                                    setShowPaymentRequestModal(true)
                                  }
                                  variant="primary"
                                  size="sm"
                                  icon="link"
                                  title={t(
                                    "dashboard.entities.viewEntityUser.createPaymentLink"
                                  )}
                                />
                              )
                            }
                          />
                        )}
                      </div>
                    </ErrorBoundary>

                    <div className="my-6 border-t border-gray-100" />

                    <UserPaymentHistory
                      customerIdentification={customer_identification}
                      entityIdentification={user.entity_identification}
                      userIdentification={user_identification as string}
                      paymentHistory={paymentHistoryData || []}
                      isLoading={isLoadingPaymentHistory}
                      isError={isErrorPaymentHistory}
                      refetch={() => {
                        usersCache.delete(
                          `user_payment_history_grouped_${user_identification}`
                        );
                        refetchPaymentHistory();
                      }}
                      highlightedLink={highlightedLink}
                      onHighlightLink={highlightPaymentLink}
                      existingPaymentLinks={
                        loadingPaymentRequests
                          ? []
                          : userPaymentRequests.map(
                              (request) => request.payment_unique_link
                            )
                      }
                      onCancelSubscription={(info) => {
                        setCancellingLink(info);
                        setShowCancelSubscriptionModal(true);
                      }}
                    />
                  </div>
                )}

                {canViewESignature && (
                  <div className="min-w-0 self-stretch bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                    <ESignatureSection
                      customerIdentification={customer_identification}
                      entityIdentification={user.entity_identification}
                      userIdentification={user_identification as string}
                      hasESignatureAccess={hasESignatureAccess}
                    />
                  </div>
                )}
              </div>
            )}

            <AppointmentsSection
              customerIdentification={customer_identification}
              entityIdentification={user.entity_identification as string}
              userIdentification={user_identification as string}
              personName={
                [userData?.user_first_name, userData?.user_last_name]
                  .filter(Boolean)
                  .join(" ") ||
                userData?.user_email ||
                undefined
              }
            />

            <GamingHistorySection
              customerIdentification={customer_identification}
              entityIdentification={user.entity_identification as string}
              userIdentification={user_identification as string}
              enabled={permissions.can(Cap.LOYALTY, Level.VIEW)}
            />

            <div className="self-stretch bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <UserEntityTabs
                entity_identification={user.entity_identification as string}
                customerId={user_identification as string}
              />
            </div>
          </div>
        </div>
      </div>

      {permissions.can(Cap.USER_NOTES, Level.VIEW) && (
        <SideDrawer
          open={showNotesDrawer}
          onClose={() => setShowNotesDrawer(false)}
          title={t("dashboard.entities.viewEntityUser.key17") as string}
          icon={HiOutlineDocumentText}
        >
          <UserNotes hideHeading />
        </SideDrawer>
      )}

      {permissions.can(Cap.CREATE_PAYMENT_LINK, Level.VIEW) && (
        <SideDrawer
          open={showAllLinksDrawer}
          onClose={() => setShowAllLinksDrawer(false)}
          title={t("dashboard.entities.viewEntityUser.paymentLinks") as string}
          icon={HiLink}
        >
          <div className="space-y-3 pb-1" ref={allLinksRef}>
            {userPaymentRequests.map((request) =>
              renderPaymentLinkRow(request)
            )}
          </div>
        </SideDrawer>
      )}

      {permissions.can(Cap.USERS_ACTIVITIES, Level.VIEW) && (
        <SideDrawer
          open={showHistoryDrawer}
          onClose={() => setShowHistoryDrawer(false)}
          title={t("tracker.title") as string}
          icon={HiOutlineClock}
        >
          <UserTracker
            userIdentification={userData.user_identification as string}
            hideHeading
          />
        </SideDrawer>
      )}

      <SendMessageChannelModal
        show={showChannelModal}
        onClose={() => setShowChannelModal(false)}
        hasEmailAccess={hasMessageAccess}
        hasSmsAccess={hasSMSAccess}
        hasWhatsappAccess={hasWhatsappAccess}
        hasEmailCapability={canSendEmail}
        hasSmsCapability={canSendSms}
        hasWhatsappCapability={canSendWhatsapp}
        hasValidPhone={!!userData.user_phone?.startsWith("+")}
        onChoose={(channel) => {
          setShowChannelModal(false);
          if (channel === "email") {
            hasMessageAccess
              ? setShowSendMsgModal(true)
              : setShowMessagingUpgradeModal(true);
          } else if (channel === "whatsapp") {
            hasWhatsappAccess
              ? setShowSendWhatsAppModal(true)
              : setShowWhatsappUpgradeModal(true);
          } else {
            hasSMSAccess
              ? setShowSendSMSModal(true)
              : setShowSMSUpgradeModal(true);
          }
        }}
      />

      <SendPersonalMessage
        show={showSendMsgModal}
        ctrFn={setShowSendMsgModal}
        userData={userData}
        sendContext={sendContext}
      />

      <SendSMSModal
        show={showSendSMSModal}
        onClose={() => setShowSendSMSModal(false)}
        onAddCredit={() => {
          setShowSendSMSModal(false);
          setShowAddCreditModal(true);
        }}
        identification={user_identification as string}
        userName={
          userData?.user_first_name && userData?.user_last_name
            ? `${userData.user_first_name} ${userData.user_last_name}`
            : userData?.user_email
        }
        userPhone={userData?.user_phone || ""}
        isSmsBlocked={isSmsBlocked}
      />

      <SendWhatsAppModal
        show={showSendWhatsAppModal}
        onClose={() => setShowSendWhatsAppModal(false)}
        onAddCredit={() => {
          setShowSendWhatsAppModal(false);
          setShowWhatsAppAddCreditModal(true);
        }}
        identification={user_identification as string}
        userName={
          userData?.user_first_name && userData?.user_last_name
            ? `${userData.user_first_name} ${userData.user_last_name}`
            : userData?.user_email
        }
        userPhone={userData?.user_phone || ""}
        recipientFields={{
          user_first_name: userData?.user_first_name || "",
          user_last_name: userData?.user_last_name || "",
          user_full_name: [userData?.user_first_name, userData?.user_last_name]
            .filter(Boolean)
            .join(" "),
          user_email: userData?.user_email || "",
          user_phone: userData?.user_phone || "",
        }}
        isWhatsappBlocked={isWhatsappBlocked}
      />

      <AddCreditModal
        show={showAddCreditModal}
        onClose={() => setShowAddCreditModal(false)}
      />

      <AddCreditModal
        kind="whatsapp"
        show={showWhatsAppAddCreditModal}
        onClose={() => setShowWhatsAppAddCreditModal(false)}
      />

      <UpgradeModal
        showModal={showMessagingUpgradeModal}
        setShowModal={setShowMessagingUpgradeModal}
        targetPlan={PlanTier.PREMIUM}
        title={t("dashboard.upgrade.messaging.title") as string}
        description={t("dashboard.upgrade.messaging.description") as string}
        benefits={messagingBenefits}
        icon={HiOutlineEnvelope}
        iconTheme="yellow"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />

      <UpgradeModal
        showModal={showSMSUpgradeModal}
        setShowModal={setShowSMSUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.sms.title") as string}
        description={t("dashboard.upgrade.sms.description") as string}
        benefits={smsBenefits}
        icon={FaSms}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />

      <UpgradeModal
        showModal={showWhatsappUpgradeModal}
        setShowModal={setShowWhatsappUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.whatsapp.title") as string}
        description={t("dashboard.upgrade.whatsapp.description") as string}
        benefits={whatsappBenefits}
        icon={FaWhatsapp}
        iconTheme="green"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />

      <PlansModal
        showPlansModal={showAiSearchUpgradeModal}
        setShowPlansModal={setShowAiSearchUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
      />

      <ModalShell
        show={openModal}
        size="md"
        onClose={() => setOpenModal(false)}
        bareCloseButton
        footer={
          <>
            <CustomButton
              variant="secondary"
              onClick={() => {
                setOpenModal(false);
                setUserToDeleteId("");
              }}
              title={t("dashboard.entities.viewEntityUser.key11")}
            />
            <CustomButton
              variant="danger"
              onClick={handleDeleteUser}
              disabled={!permissions.can(Cap.USERS_DELETE, Level.DELETE)}
              title={t("dashboard.entities.viewEntityUser.key10")}
            />
          </>
        }
      >
        <div className="text-center">
          <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t("dashboard.entities.viewEntityUser.key9")}
          </h3>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("dashboard.entities.viewEntityUser.key9-1")}
          </p>
        </div>
      </ModalShell>

      {userData && (
        <CreatePaymentRequestModal
          show={showPaymentRequestModal}
          onClose={() => setShowPaymentRequestModal(false)}
          userIdentification={userData.user_identification}
          userName={
            userData.user_first_name && userData.user_last_name
              ? `${userData.user_first_name} ${userData.user_last_name}`
              : userData.user_email
          }
          onSuccess={() => fetchUserPaymentRequests()}
        />
      )}

      {userData && (
        <CreatePaymentRequestModal
          show={!!viewingPaymentRequest}
          onClose={() => setViewingPaymentRequest(null)}
          existingPaymentRequest={viewingPaymentRequest || undefined}
          userIdentification={
            viewingPaymentRequest?.user_identification || undefined
          }
          userName={
            userData.user_first_name && userData.user_last_name
              ? `${userData.user_first_name} ${userData.user_last_name}`
              : userData.user_email
          }
        />
      )}

      <ModalShell
        show={showDeletePaymentLinkModal}
        onClose={() => {
          setShowDeletePaymentLinkModal(false);
          setDeletingPaymentLink(null);
        }}
        footer={
          <>
            <CustomButton
              onClick={() => {
                setShowDeletePaymentLinkModal(false);
                setDeletingPaymentLink(null);
              }}
              title={t("common.cancel") as string}
              disabled={isDeletingPaymentLink}
            />
            <CustomButton
              variant="danger"
              onClick={handleDeletePaymentLink}
              title={
                isDeletingPaymentLink
                  ? (t("common.deleting") as string)
                  : (t("common.delete") as string)
              }
              disabled={
                isDeletingPaymentLink ||
                !permissions.can(Cap.CREATE_PAYMENT_LINK, Level.WRITE)
              }
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <HiOutlineTrash size={24} className="text-red-500" />
          </div>

          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("dashboard.entities.viewEntityUser.deletePaymentLink.title")}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {t(
                "dashboard.entities.viewEntityUser.deletePaymentLink.description"
              )}
            </p>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={showCancelSubscriptionModal}
        onClose={() => {
          setShowCancelSubscriptionModal(false);
          setCancellingLink(null);
        }}
        footer={
          <>
            <CustomButton
              onClick={() => {
                setShowCancelSubscriptionModal(false);
                setCancellingLink(null);
              }}
              title={
                t(
                  "paymentModule.cancelSubscriptionModal.keepSubscription"
                ) as string
              }
              disabled={cancellingMode !== null}
            />
            <CustomButton
              variant="secondary"
              onClick={() => handleCancelSubscription(false)}
              title={
                cancellingMode === "end_of_period"
                  ? (t(
                      "paymentModule.cancelSubscriptionModal.cancelling"
                    ) as string)
                  : (t(
                      "paymentModule.cancelSubscriptionModal.cancelAtEndOfPeriod"
                    ) as string)
              }
              disabled={cancellingMode !== null}
            />
            <CustomButton
              variant="danger"
              onClick={() => handleCancelSubscription(true)}
              title={
                cancellingMode === "immediate"
                  ? (t(
                      "paymentModule.cancelSubscriptionModal.cancelling"
                    ) as string)
                  : (t(
                      "paymentModule.cancelSubscriptionModal.cancelImmediately"
                    ) as string)
              }
              disabled={cancellingMode !== null}
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <HiBan size={24} className="text-red-500" />
          </div>

          <div className="flex-col justify-start items-start gap-2 inline-flex w-full">
            <div className="text-gray-900 text-lg font-semibold leading-7">
              {t("paymentModule.cancelSubscriptionModal.title")}
            </div>
            <div className="text-gray-500 text-sm leading-tight">
              {t("paymentModule.cancelSubscriptionModal.question")}
            </div>
            {cancellingLink && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 w-full">
                <p className="text-sm font-medium text-gray-700">
                  {cancellingLink.description ||
                    t("paymentModule.cancelSubscriptionModal.recurringPayment")}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Intl.NumberFormat(i18next.language || "en", {
                    style: "currency",
                    currency: cancellingLink.currency,
                    minimumFractionDigits: 2,
                  }).format(cancellingLink.amount / 100)}
                  {cancellingLink.paymentType &&
                    cancellingLink.paymentType !== "UNIQUE" &&
                    cancellingLink.paymentType !== "SUBSCRIPTION" && (
                      <span>
                        {cancellingLink.paymentType === "ANNUALLY"
                          ? t("paymentModule.cancelSubscriptionModal.perYear")
                          : cancellingLink.paymentType === "WEEKLY"
                          ? t("paymentModule.cancelSubscriptionModal.perWeek")
                          : cancellingLink.paymentType === "DAILY"
                          ? t("paymentModule.cancelSubscriptionModal.perDay")
                          : t("paymentModule.cancelSubscriptionModal.perMonth")}
                      </span>
                    )}
                </p>
              </div>
            )}
            <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg w-full text-center">
              <p className="text-amber-700 text-xs font-medium">
                {t("paymentModule.cancelSubscriptionModal.warning")}
              </p>
            </div>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={editEmail}
        onClose={() => {
          setEditEmail(false);
          resetEmail({
            new_user_email: "",
            new_user_email_confirmation: "",
          });
        }}
        title={t("dashboard.entities.viewEntityUser.updateEmail")}
        onSubmit={handleEmailSubmit(onEmailSubmit)}
        footer={
          <>
            <CustomButton
              variant="gray"
              onClick={() => {
                setEditEmail(false);
                resetEmail({
                  new_user_email: "",
                  new_user_email_confirmation: "",
                });
              }}
              title={t("common.cancel") as string}
            />
            <CustomButton
              type="submit"
              variant="primary"
              disabled={
                isUpdatingEmail ||
                !permissions.can(Cap.USERS_UPDATE, Level.WRITE)
              }
              isLoading={isUpdatingEmail}
              title={t("common.saveShort") as string}
            />
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex-col justify-start items-start gap-1 flex w-full">
            <div className="self-stretch text-gray-500 text-xs leading-none">
              {t("dashboard.entities.viewEntityUser.currentEmail")}
            </div>
            <div className="self-stretch text-gray-700 text-base font-semibold leading-normal">
              <NoTranslate>{userData?.user_email}</NoTranslate>
            </div>
          </div>

          <div className="flex-col justify-start items-start gap-1 flex w-full">
            <div className="self-stretch text-gray-500 text-xs leading-none">
              {t("dashboard.entities.viewEntityUser.newEmail")}
            </div>
            <CustomInput
              type="email"
              placeholder=""
              name="new_user_email"
              control={emailControl}
              error={emailErrors.new_user_email}
              helperText={emailErrors.new_user_email?.message}
            />
          </div>

          <div className="flex-col justify-start items-start gap-1 flex w-full">
            <div className="self-stretch text-gray-500 text-xs leading-none">
              {t("dashboard.entities.viewEntityUser.confirmEmail")}
            </div>
            <CustomInput
              type="email"
              placeholder=""
              name="new_user_email_confirmation"
              control={emailControl}
              error={emailErrors.new_user_email_confirmation}
              helperText={emailErrors.new_user_email_confirmation?.message}
            />
          </div>
        </div>
      </ModalShell>

      <PhoneValidationModal
        show={showPhoneVerificationModal}
        onClose={() => setShowPhoneVerificationModal(false)}
        onConfirm={handleConfirmPhoneVerification}
        onResend={handleStartPhoneVerification}
        expiresAt={phoneValidationExpiresAt}
        loading={isStartingPhoneValidation || isConfirmingPhoneValidation}
        phoneNumber={userData.user_phone || ""}
      />

      <ImportFromDocument
        showModal={openDocumentModal}
        setShowModal={setOpenDocumentModal}
        userIdentification={userData.user_identification}
      />
    </div>
  );
};

export default ViewEntityUser;
